/**
 * Terrain-mask fetching, the orchestration half of loader-terrain.js: after a
 * dataset loads, up to 60 antennas without coverage.mask get a horizon mask
 * through fetchTerrainMask (analysis module) with the elevation proxy as the
 * source and the localStorage cache, one request at a time with a 250 ms
 * pause, then the dataset is patched once with the masks filled in. Skipped
 * for live-constellation datasets with more than 60 antennas.
 */
import { fetchTerrainMask, type TerrainMaskDeps } from '../analysis';
import { localStorageMaskCache } from '../analysis/terrainMaskCache';
import type { TerrainMask } from '../analysis/types';
import { loaderApi } from '../data/api';
import type { Antenna, NetworkDataset } from '../types';
import { useAwareness } from './store';
import { errorMessage, sleep } from './util';

export const TERRAIN_ANTENNA_LIMIT = 60;
export const TERRAIN_REQUEST_DELAY_MS = 250;

export function hasMask(antenna: Antenna): boolean {
  return Array.isArray(antenna.coverage?.mask) && antenna.coverage.mask.length > 0;
}

/** Antennas without a mask, the first 60 in dataset order. */
export function antennasNeedingMasks(antennas: readonly Antenna[], limit: number = TERRAIN_ANTENNA_LIMIT): Antenna[] {
  return antennas.filter((a) => a.location && !hasMask(a)).slice(0, limit);
}

/** Live constellations above the cap are skipped; everything else is fetched. */
export function shouldFetchMasks(dataset: Pick<NetworkDataset, 'source' | 'antennas'>): boolean {
  return !(dataset.source === 'live' && dataset.antennas.length > TERRAIN_ANTENNA_LIMIT);
}

/** Elevations for the sample points through /api/loader/elevation, in order. */
export async function fetchElevations(points: { lat: number; lon: number }[]): Promise<(number | null)[]> {
  const res = await loaderApi.elevation(points);
  return (res.results || []).map((r) => (r.elevation_m === undefined ? null : r.elevation_m));
}

export interface MaskFetchOptions {
  fetchMask?: (lat: number, lon: number) => Promise<TerrainMask>;
  delayMs?: number;
  signal?: AbortSignal;
  onProgress?: (done: number, total: number, failed: number) => void;
}

function defaultFetchMask(lat: number, lon: number): Promise<TerrainMask> {
  const deps: TerrainMaskDeps = { fetchElevations, cache: localStorageMaskCache() };
  return fetchTerrainMask(lat, lon, deps);
}

/**
 * Masks for the given antennas, sequentially with the delay between requests.
 * Failures leave the antenna without a mask (first failure logged, a summary at
 * the end). Resolves with antennaId -> mask for the successes; stops early when
 * the signal aborts.
 */
export async function fetchMasks(antennas: readonly Antenna[], options: MaskFetchOptions = {}): Promise<Map<string, TerrainMask>> {
  const fetchMask = options.fetchMask ?? defaultFetchMask;
  const delay = options.delayMs ?? TERRAIN_REQUEST_DELAY_MS;
  const results = new Map<string, TerrainMask>();
  let failed = 0;
  let firstFailureLogged = false;
  for (let i = 0; i < antennas.length; i++) {
    if (options.signal?.aborted) break;
    const ant = antennas[i];
    try {
      results.set(ant.id, await fetchMask(ant.location.lat, ant.location.lon));
    } catch (err) {
      failed++;
      if (!firstFailureLogged) {
        console.warn(`[terrain] first mask fetch failure (${ant.id}):`, errorMessage(err));
        firstFailureLogged = true;
      }
    }
    options.onProgress?.(i + 1, antennas.length, failed);
    if (delay > 0 && i + 1 < antennas.length && !options.signal?.aborted) await sleep(delay);
  }
  if (failed > 0) {
    console.warn(`[terrain] ${failed}/${antennas.length} mask fetches failed (likely DEM API rate limit); those antennas keep plain coverage circles.`);
  }
  return results;
}

/** The antenna list with the fetched masks filled in (new objects for the patched antennas only). */
export function applyMasks(antennas: readonly Antenna[], masks: ReadonlyMap<string, TerrainMask>): Antenna[] {
  return antennas.map((a) => {
    const mask = masks.get(a.id);
    return mask ? { ...a, coverage: { ...a.coverage, mask } } : a;
  });
}

/**
 * Fetch the missing masks for a dataset and hand them to `applyPatch` once at
 * the end (the runtime patches the dataset current at that moment, so a sync
 * poll in between is not undone). Progress is published in the awareness
 * store. Returns the function that aborts the run (a network switch).
 */
export function startTerrainMasks(
  dataset: NetworkDataset,
  applyPatch: (masks: Map<string, TerrainMask>) => void,
  options: MaskFetchOptions = {},
): () => void {
  const controller = new AbortController();
  const store = useAwareness.getState();
  if (!shouldFetchMasks(dataset)) return () => controller.abort();
  const targets = antennasNeedingMasks(dataset.antennas);
  if (targets.length === 0) return () => controller.abort();
  store.setTerrainProgress({ done: 0, total: targets.length, failed: 0 });
  (async () => {
    try {
      const masks = await fetchMasks(targets, {
        ...options,
        signal: controller.signal,
        onProgress: (done, total, failed) => {
          if (!controller.signal.aborted) useAwareness.getState().setTerrainProgress({ done, total, failed });
          options.onProgress?.(done, total, failed);
        },
      });
      if (controller.signal.aborted) return;
      if (masks.size > 0) applyPatch(masks);
    } catch (err) {
      console.error('[terrain] mask run failed:', err);
    } finally {
      if (!controller.signal.aborted) useAwareness.getState().setTerrainProgress(null);
    }
  })();
  return () => {
    controller.abort();
    useAwareness.getState().setTerrainProgress(null);
  };
}
